import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Paper, CardActions } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { AppState, AppThunkDispatch } from 'src/store';
import { getScopeReadyData } from 'src/state/scope/Scope.types';
import { isEmpty, isNil } from 'lodash';
import { requestAddPrivateVersion } from 'src/state/scope/Scope.actions';
import { toast } from 'react-toastify';
import { getImportPrivateCommands, hasCommands } from 'src/state/scope/codecs/Commands.utils';
import { useFocusDropdown, useVersionState } from 'src/utils/Component/hooks/PlanManagement.hooks';

export type VersionModalBodyComponentProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof dispatchToProps> & {
    loading: boolean;
    onCancel?: () => void;
    onSubmit?: (name?: string) => void;
    text?: string;
    smartSave?: boolean; // this is used only for smartsave.tsx to bypass the redux save
  };

const mapStateToProps = (state: AppState) => {
  const readyScope = getScopeReadyData(state.mfpScope);
  const { settings } = state;

  if (!readyScope || isEmpty(readyScope.commands)) {
    return;
  }

  // map it twice, as hasCommands doesn't return the correct type
  // filter so that we don't get empty sets of commands
  const commands = readyScope.commands.map(getImportPrivateCommands).filter(hasCommands).map(getImportPrivateCommands);

  return {
    commands,
    scope: readyScope,
    pendingWrites: readyScope.pendingWrites > 0,
    entries: settings.entriesByKey,
  };
};

const dispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    dispatchedRequestAddPrivateVersion: (version: { versionName: string; smartSave: boolean }) => {
      return dispatch(requestAddPrivateVersion(version));
    },
  };
};

type Option = { text: string; value: string };

const SaveVersion = (props: VersionModalBodyComponentProps) => {
  const {
    onCancel,
    onSubmit,
    commands,
    dispatchedRequestAddPrivateVersion,
    text = 'WP',
    smartSave = false,
    pendingWrites,
    entries,
  } = props;

  const ddRef = useFocusDropdown();
  const loading = isNil(commands);
  const [mutationPending, setMutationPending] = useState(false);

  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [versionOptions, setVersionOptions] = useVersionState(commands, entries);

  const handleSubmit = useCallback(
    async (options: { text: string; value: string }[] | undefined, selectedVersion: string | undefined) => {
      if (selectedVersion && onSubmit && options && commands) {
        const verToSubmit = options.find((o) => o.value === selectedVersion);
        setMutationPending(true);
        if (!verToSubmit || !verToSubmit.text) {
          setMutationPending(false);
          return;
        }
        const addVersionPromise = await dispatchedRequestAddPrivateVersion({
          versionName: verToSubmit.text.toString(),
          smartSave,
        });
        if (addVersionPromise.type === requestAddPrivateVersion.fulfilled.type) {
          onSubmit(); // only close the modal on success
        } else if (addVersionPromise.type === requestAddPrivateVersion.rejected.type) {
          toast.error('An error occured submitting your version', {
            position: toast.POSITION.TOP_LEFT,
          });
        }
        setMutationPending(false);
      }
    },
    [commands, dispatchedRequestAddPrivateVersion, onSubmit, smartSave],
  );

  const handleOnSave = useCallback(() => {
    // TODO: modal to confirm if overwriting an existing version?
    handleSubmit(versionOptions, selectedVersion);
  }, [handleSubmit, versionOptions, selectedVersion]);

  const handleOnAdd = useCallback(
    (e: React.ChangeEvent<{}>, data: { value: string }) => {
      const { value } = data;
      if (typeof value !== 'string' || !commands || !versionOptions) {
        return;
      }
      const newOptions = [...versionOptions, { text: value, value: value }];
      setSelectedVersion(value);
      setVersionOptions(newOptions);
    },
    [commands, versionOptions],
  );

  const handleOnChange = useCallback((_event: React.ChangeEvent<{}>, data: { value: string }) => {
    if (data.value && typeof data.value === 'string') {
      setSelectedVersion(data.value);
    }
  }, []);

  const handleEnterPress = useCallback(
    (evt: React.KeyboardEvent<HTMLElement>) => {
      if (evt.key === 'Enter') {
        handleSubmit(versionOptions, selectedVersion);
      }
    },
    [handleSubmit, selectedVersion, versionOptions],
  );

  return (
    <>
      <div data-qa="version-save-container" className="save-version">
        <Paper variant="outlined" style={{ padding: '1em', marginBottom: '1em' }}>
          Enter a name to save your {text} as, or select an existing name to overwrite that version with your {text}
        </Paper>
        <div ref={ddRef}>
          <Autocomplete
            options={versionOptions || []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.text)}
            loading={loading}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                // User typed something and pressed enter
                handleOnAdd(_, { value: newValue });
              } else if (newValue && !versionOptions?.some((opt) => opt.value === newValue.value)) {
                // User selected an "Add" option
                handleOnAdd(_, { value: newValue.value });
              } else {
                // Standard selection change
                handleOnChange(_, { value: newValue ? newValue.value : '' });
              }
            }}
            value={versionOptions?.find((opt) => opt.value === selectedVersion) || undefined}
            popupIcon={<ArrowDropDownIcon style={{ color: '#000' }} />}
            renderInput={(params) => (
              <TextField {...params} className="text-field" variant="outlined" onKeyPress={handleEnterPress} />
            )}
            className="autoComplete"
            disableClearable
            filterOptions={(options, params) => {
              const { inputValue } = params;
              const filtered = options.filter((option) => option.text.toLowerCase().includes(inputValue.toLowerCase()));
              // If the typed value is not present, provide an Add option
              if (inputValue !== '' && !options.some((opt) => opt.text.toLowerCase() === inputValue.toLowerCase())) {
                filtered.push({
                  text: `Add "${inputValue}"`,
                  value: inputValue,
                });
              }
              return filtered;
            }}
            selectOnFocus
            clearOnBlur
          />
        </div>
      </div>
      <CardActions>
        <Button className="button" variant="contained" onClick={onCancel}>
          Close
        </Button>
        <Button
          onClick={handleOnSave}
          disabled={pendingWrites}
          className="save-version-modal-button button"
          data-qa="version-save-btn-save"
          onKeyPress={handleEnterPress}
        >
          {loading || mutationPending || pendingWrites ? 'Saving...' : 'Save'}
        </Button>
      </CardActions>
    </>
  );
};

export default connect(mapStateToProps, dispatchToProps)(SaveVersion);
